import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, clearToken, getToken, setSessionLostHandler, setToken, type Me } from './api';

interface SessionValue {
  me: Me | null;
  loading: boolean;
  notice: string;
  login: (token: string) => void;
  logout: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => Boolean(getToken()));
  const [notice, setNotice] = useState('');

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api<Me>('/auth/me'),
    enabled: hasToken,
    retry: false,
    staleTime: 60_000
  });

  const logout = useCallback(() => {
    clearToken();
    setHasToken(false);
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    setSessionLostHandler((message) => {
      setNotice(message);
      setHasToken(false);
      queryClient.clear();
    });
  }, [queryClient]);

  const login = useCallback((token: string) => {
    setToken(token);
    setNotice('');
    setHasToken(true);
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  const value: SessionValue = {
    me: hasToken ? meQuery.data ?? null : null,
    loading: hasToken && meQuery.isLoading,
    notice,
    login,
    logout
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession fuera de SessionProvider');
  return value;
}
